import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Mail, Plus, Trash2, Pencil, PlugZap, Loader2, Eye, EyeOff,
  CheckCircle2, XCircle, ExternalLink, ChevronDown, ChevronUp, HelpCircle,
} from 'lucide-react';

// Presets pour faciliter la saisie
const PRESETS = {
  custom: { label: 'Personnalisé', host: '', port: 993 },
  ionos: { label: 'IONOS / 1&1', host: 'imap.ionos.fr', port: 993 },
  gmail: { label: 'Gmail (App Password requis)', host: 'imap.gmail.com', port: 993 },
  ovh: { label: 'OVH', host: 'ssl0.ovh.net', port: 993 },
  outlook: { label: 'Outlook / Hotmail', host: 'outlook.office365.com', port: 993 },
  free: { label: 'Free Pro', host: 'imap.free.fr', port: 993 },
  laposte: { label: 'La Poste', host: 'imap.laposte.net', port: 993 },
};

const emptyForm = () => ({
  id: null,
  preset: 'custom',
  label: '',
  imap_host: '',
  imap_port: 993,
  imap_user: '',
  imap_password: '',
  folder: 'INBOX',
  enabled: true,
});

export default function MailboxesSettingsCard() {
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [showHelp, setShowHelp] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/imap-mailboxes');
      setMailboxes(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      // Non-admin ou pas encore configuré
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const applyPreset = (key) => {
    const p = PRESETS[key] || PRESETS.custom;
    setForm((f) => ({ ...f, preset: key, imap_host: p.host, imap_port: p.port }));
  };

  const openCreate = () => {
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (m) => {
    setForm({
      id: m.id,
      preset: detectPreset(m.imap_host),
      label: m.label || '',
      imap_host: m.imap_host || '',
      imap_port: m.imap_port || 993,
      imap_user: m.imap_user || '',
      imap_password: '', // never echoed
      folder: m.folder || 'INBOX',
      enabled: m.enabled !== false,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.label.trim() || !form.imap_host.trim() || !form.imap_user.trim()) {
      toast.error('Label, serveur IMAP et adresse email requis');
      return;
    }
    if (!form.id && !form.imap_password) {
      toast.error('Le mot de passe est requis à la création');
      return;
    }
    setSaving(true);
    try {
      const body = {
        label: form.label.trim(),
        imap_host: form.imap_host.trim(),
        imap_port: Number(form.imap_port) || 993,
        imap_user: form.imap_user.trim(),
        folder: form.folder || 'INBOX',
        enabled: form.enabled,
      };
      if (form.imap_password) body.imap_password = form.imap_password;

      if (form.id) {
        await api.put(`/imap-mailboxes/${form.id}`, body);
        toast.success(`Boîte « ${body.label} » mise à jour`);
      } else {
        await api.post('/imap-mailboxes', body);
        toast.success(`Boîte « ${body.label} » ajoutée`);
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
    setSaving(false);
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Supprimer la boîte « ${m.label} » ? Le scan ne l'utilisera plus.`)) return;
    try {
      await api.delete(`/imap-mailboxes/${m.id}`);
      toast.success('Boîte supprimée');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleTest = async (m) => {
    setTestingId(m.id);
    try {
      const r = await api.post(`/imap-mailboxes/${m.id}/test`);
      toast.success(r.data?.message || 'Connexion IMAP OK');
      load();
    } catch (e) {
      let detail = e?.response?.data?.detail;
      if (detail && typeof detail !== 'string') {
        try { detail = JSON.stringify(detail); } catch (_) { detail = String(detail); }
      }
      toast.error(detail || 'Échec du test de connexion', { duration: 10000 });
      load();
    }
    setTestingId(null);
  };

  if (loading) return null;

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200" data-testid="mailboxes-settings-card">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
            <Mail className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Boîtes mail à scanner</h2>
            <p className="text-xs text-slate-500 mt-1">
              Le CRM scanne toutes les boîtes activées toutes les heures pour importer les factures fournisseurs reçues par email.
            </p>
          </div>
        </div>
        <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mailbox-add-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Ajouter une boîte
        </Button>
      </div>

      {mailboxes.length === 0 && !showForm && (
        <div key="mb-empty" className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900" data-testid="mailbox-empty">
          Aucune boîte mail configurée. Cliquez sur « Ajouter une boîte » pour commencer.
        </div>
      )}

      {/* Liste */}
      {mailboxes.length > 0 && (
        <div key="mb-list" className="space-y-2 mb-4" data-testid="mailbox-list">
          {mailboxes.map((m) => (
            <div
              key={m.id}
              className={`rounded-lg border p-3 flex items-center gap-3 ${
                m.enabled ? 'border-slate-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-70'
              }`}
              data-testid={`mailbox-row-${m.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-900 truncate">{m.label}</span>
                  {!m.enabled && <Badge variant="outline" className="text-xs">Désactivée</Badge>}
                  {m.last_test_status === 'ok'
                    ? <Badge key={`ok-${m.id}`} className="bg-emerald-100 text-emerald-700 text-xs">
                        <CheckCircle2 className="w-3 h-3 mr-1" />Testée OK
                      </Badge>
                    : m.last_test_status === 'ko' && (
                        <Badge key={`ko-${m.id}`} className="bg-red-100 text-red-700 text-xs">
                          <XCircle className="w-3 h-3 mr-1" />Échec
                        </Badge>
                      )}
                </div>
                <p className="text-xs text-slate-500 truncate">
                  {m.imap_user} · {m.imap_host}:{m.imap_port} · {m.folder}
                </p>
                {m.last_test_status === 'ko' && m.last_test_message && (
                  <p className="text-[11px] text-red-600 mt-0.5 line-clamp-2">{m.last_test_message}</p>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTest(m)}
                disabled={testingId === m.id}
                data-testid={`mailbox-test-${m.id}`}
              >
                {testingId === m.id
                  ? <Loader2 key="t-load" className="w-3.5 h-3.5 mr-1 animate-spin" />
                  : <PlugZap key="t-icon" className="w-3.5 h-3.5 mr-1" />}
                Tester
              </Button>
              <Button variant="ghost" size="sm" onClick={() => openEdit(m)} data-testid={`mailbox-edit-${m.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(m)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid={`mailbox-delete-${m.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Formulaire create/edit */}
      {showForm && (
        <div key={`mb-form-${form.id || 'new'}`} className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3" data-testid="mailbox-form">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">
              {form.id ? 'Modifier la boîte mail' : 'Nouvelle boîte mail'}
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>✕</Button>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Fournisseur</label>
            <select
              value={form.preset}
              onChange={(e) => applyPreset(e.target.value)}
              className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
              data-testid="mailbox-preset"
            >
              {Object.entries(PRESETS).map(([k, p]) => (
                <option key={k} value={k}>{p.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Label</label>
              <Input
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="Ex: Boîte principale, Gmail comptabilité…"
                data-testid="mailbox-label"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Dossier IMAP</label>
              <Input
                value={form.folder}
                onChange={(e) => setForm((f) => ({ ...f, folder: e.target.value }))}
                placeholder="INBOX"
                data-testid="mailbox-folder"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Serveur IMAP</label>
              <Input
                value={form.imap_host}
                onChange={(e) => setForm((f) => ({ ...f, imap_host: e.target.value }))}
                placeholder="imap.gmail.com"
                data-testid="mailbox-host"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Port</label>
              <Input
                type="number"
                value={form.imap_port}
                onChange={(e) => setForm((f) => ({ ...f, imap_port: e.target.value }))}
                placeholder="993"
                data-testid="mailbox-port"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Adresse email</label>
              <Input
                type="email"
                value={form.imap_user}
                onChange={(e) => setForm((f) => ({ ...f, imap_user: e.target.value }))}
                placeholder="contact@exemple.com"
                data-testid="mailbox-user"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Mot de passe {form.id && <span className="normal-case font-normal text-slate-400">(laissez vide pour conserver)</span>}
              </label>
              <div className="relative">
                <Input
                  type={showPwd ? 'text' : 'password'}
                  value={form.imap_password}
                  onChange={(e) => setForm((f) => ({ ...f, imap_password: e.target.value }))}
                  placeholder={form.id ? '••••••••' : 'Saisir le mot de passe'}
                  data-testid="mailbox-password"
                />
                <button type="button" onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPwd
                    ? <EyeOff key="hide-pwd" className="w-4 h-4" />
                    : <Eye key="show-pwd" className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="mailbox-enabled"
              checked={form.enabled}
              onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              data-testid="mailbox-enabled"
            />
            <label htmlFor="mailbox-enabled" className="text-sm text-slate-700">
              Activée — incluse dans le scan automatique
            </label>
          </div>

          {form.preset === 'gmail' && (
            <div key="gmail-help" className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900 space-y-1">
              <p className="font-semibold">⚠️ Gmail nécessite un mot de passe d'application</p>
              <ol className="list-decimal list-inside space-y-0.5 ml-1">
                <li>Activez la <b>validation en 2 étapes</b> sur votre compte Google</li>
                <li>Allez sur <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-blue-700 hover:underline inline-flex items-center gap-0.5">myaccount.google.com/apppasswords <ExternalLink className="w-3 h-3" /></a></li>
                <li>Générez un mot de passe pour « Mail » → copiez les 16 caractères ci-dessus</li>
              </ol>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowForm(false)} disabled={saving}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="mailbox-save-btn">
              {saving
                ? <Loader2 key="save-load" className="w-4 h-4 mr-1.5 animate-spin" />
                : <CheckCircle2 key="save-icon" className="w-4 h-4 mr-1.5" />}
              {form.id ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </div>
        </div>
      )}

      {/* Help */}
      <div className="mt-4 border-t border-slate-200 pt-3">
        <button
          type="button"
          onClick={() => setShowHelp((v) => !v)}
          className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-blue-700"
        >
          <HelpCircle className="w-4 h-4" />
          Comment ça marche ?
          {showHelp
            ? <ChevronUp key="help-up" className="w-4 h-4" />
            : <ChevronDown key="help-down" className="w-4 h-4" />}
        </button>
        {showHelp && (
          <div key="help-box" className="mt-3 bg-blue-50/60 border border-blue-200 rounded-lg p-4 text-sm text-slate-700 space-y-2">
            <p><b>Scan automatique :</b> chaque heure, le CRM se connecte à chaque boîte mail activée, cherche les emails non lus reçus dans les 30 derniers jours avec une pièce jointe PDF ou photo.</p>
            <p><b>Filtres anti-faux-positifs :</b> mots-clés dans l'objet/corps (facture, invoice, facturation…) + validation par OCR (présence d'un fournisseur + d'un montant TTC).</p>
            <p><b>Anti-doublons :</b> empreinte SHA-256 du PDF + Message-ID de l'email. Un email retraité ne crée jamais de doublon.</p>
            <p><b>Marquage :</b> les emails ayant produit une facture sont marqués comme lus dans la boîte mail pour ne pas être re-traités.</p>
            <p>Vous pouvez aussi déclencher un scan manuel via le bouton « Scanner les emails » dans la page <b>Factures d'achat</b>.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function detectPreset(host) {
  if (!host) return 'custom';
  const h = host.toLowerCase();
  if (h.includes('ionos')) return 'ionos';
  if (h.includes('gmail')) return 'gmail';
  if (h.includes('ovh')) return 'ovh';
  if (h.includes('office365') || h.includes('outlook')) return 'outlook';
  if (h.includes('free.fr')) return 'free';
  if (h.includes('laposte')) return 'laposte';
  return 'custom';
}
